import { useCallback, useState } from 'react';

import {
  createBarrier as apiCreate,
  deleteBarrier as apiDelete,
  replaceBarrier as apiReplace,
} from '@/api/endpoints/barriers';
import type {
  BarrierDeletionResponse,
  BarrierMutationResponse,
  BarrierRequest,
} from '@/types/barriers';

interface UseBarrierMutationsValue {
  readonly saving: boolean;
  readonly create: (body: BarrierRequest) => Promise<BarrierMutationResponse>;
  readonly replace: (
    id: number, body: BarrierRequest,
  ) => Promise<BarrierMutationResponse>;
  readonly remove: (id: number) => Promise<BarrierDeletionResponse>;
}

export function useBarrierMutations(): UseBarrierMutationsValue {
  const [saving, setSaving] = useState(false);

  const create = useCallback(async (body: BarrierRequest) => {
    setSaving(true);
    try {
      return await apiCreate(body);
    } finally {
      setSaving(false);
    }
  }, []);

  const replace = useCallback(async (id: number, body: BarrierRequest) => {
    setSaving(true);
    try {
      return await apiReplace(id, body);
    } finally {
      setSaving(false);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setSaving(true);
    try {
      return await apiDelete(id);
    } finally {
      setSaving(false);
    }
  }, []);

  return { saving, create, replace, remove };
}
